/**
 * CollapsibleSidebar
 *
 * Generic collapsible sidebar component that can be used on left or right side of a page.
 * Smoothly transitions between expanded and collapsed states.
 *
 * Usage:
 * ```tsx
 * const [collapsed, setCollapsed] = useState(false);
 *
 * <CollapsibleSidebar
 *   side="right"
 *   isCollapsed={collapsed}
 *   onToggle={() => setCollapsed(!collapsed)}
 * >
 *   <YourSidebarContent />
 * </CollapsibleSidebar>
 * ```
 */

import { Box, IconButton, VStack } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type React from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';

export interface CollapsibleSidebarProps {
  /** Which side of the layout this sidebar is on */
  side: 'left' | 'right';

  /** Whether the sidebar is currently collapsed */
  isCollapsed: boolean;

  /** Handler called when toggle button is clicked */
  onToggle: () => void;

  /** Width when expanded (in px) */
  expandedWidth?: number;

  /** Width when collapsed (in px) */
  collapsedWidth?: number;

  /** Background color */
  bg?: string;

  /** Border color */
  borderColor?: string;

  /** Content to display in sidebar */
  children: React.ReactNode;

  /** Optional className for styling */
  className?: string;
}

export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  side,
  isCollapsed,
  onToggle,
  expandedWidth = LAYOUT_SPACING.SIDEBAR_EXPANDED_SM,
  collapsedWidth = LAYOUT_SPACING.SIDEBAR_COLLAPSED,
  bg = 'white',
  borderColor = 'gray.200',
  children,
  className,
}) => {
  // Determine chevron direction based on side and collapsed state
  const ChevronIcon = () => {
    if (side === 'left') {
      return isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />;
    } else {
      return isCollapsed ? <ChevronLeft size={20} /> : <ChevronRight size={20} />;
    }
  };

  // Border position based on side
  const borderPosition = side === 'left' ? 'borderRight' : 'borderLeft';

  return (
    <Box
      width={isCollapsed ? `${collapsedWidth}px` : `${expandedWidth}px`}
      minWidth={isCollapsed ? `${collapsedWidth}px` : `${expandedWidth}px`}
      height="100%"
      bg={bg}
      {...{ [borderPosition]: '1px solid' }}
      borderColor={borderColor}
      position="relative"
      transition={`width ${LAYOUT_SPACING.TRANSITION_NORMAL}, min-width ${LAYOUT_SPACING.TRANSITION_NORMAL}`}
      overflow="hidden"
      className={className}
    >
      {/* Toggle Button */}
      <IconButton
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        size="xs"
        variant="ghost"
        onClick={onToggle}
        position="absolute"
        top={2}
        {...{ [side === 'left' ? 'right' : 'left']: 2 }}
        zIndex={10}
      >
        <ChevronIcon />
      </IconButton>

      {/* Content Container */}
      <Box
        width={isCollapsed ? `${collapsedWidth}px` : `${expandedWidth}px`}
        height="100%"
        overflowY="auto"
        overflowX="hidden"
        pt={10} // Space for toggle button
        px={isCollapsed ? 1 : 3}
        pb={3}
      >
        {isCollapsed ? (
          // Collapsed state: vertical layout for icons/badges
          <VStack gap={2} align="center" width="100%">
            {children}
          </VStack>
        ) : (
          // Expanded state: normal layout
          <Box width="100%">{children}</Box>
        )}
      </Box>
    </Box>
  );
};
